/**
 * Captures agent audio from active Twilio call and sends to Deepgram for transcription
 * Works alongside customer audio stream from Twilio Media Streams
 */

import { useEffect, useRef } from 'react';
import { Call } from '@twilio/voice-sdk';
import { supabase } from '@/lib/supabase';

interface UseAgentAudioCaptureProps {
  activeCall: Call | null;
  callSid: string | null;
  isCallActive: boolean;
}

export function useAgentAudioCapture({ 
  activeCall, 
  callSid,
  isCallActive 
}: UseAgentAudioCaptureProps) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const deepgramWsRef = useRef<WebSocket | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);

  useEffect(() => {
    if (!activeCall || !callSid || !isCallActive) {
      // Cleanup if call ends
      cleanup();
      return;
    }

    console.log('[AgentAudio] Setting up agent audio capture for call:', callSid);
    setupAgentAudioCapture();

    return () => {
      cleanup();
    };
  }, [activeCall, callSid, isCallActive]);

  const setupAgentAudioCapture = async () => {
    try {
      // Get the agent's microphone audio stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });

      console.log('[AgentAudio] ✅ Got microphone stream');

      // Connect to agent audio proxy (server-side Deepgram)
      // Build WebSocket URL from Supabase URL
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const wsBase = supabaseUrl ? supabaseUrl.replace('https://', 'wss://') : '';
      const { data: userData } = await supabase.auth.getUser();
      const agentId = userData?.user?.id || 'unknown';

      const proxyUrl = `${wsBase}/functions/v1/agent-audio-proxy?` + new URLSearchParams({
        callSid: callSid || '',
        agentId,
      }).toString();

      const ws = new WebSocket(proxyUrl);
      deepgramWsRef.current = ws;

      ws.onopen = () => {
        console.log('[AgentAudio] ✅ Connected to agent-audio-proxy');
        startRecording(stream, ws);
      };

      ws.onerror = (error) => {
        console.error('[AgentAudio] ❌ Proxy WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('[AgentAudio] 🔌 Proxy WebSocket closed');
      };

    } catch (error) {
      console.error('[AgentAudio] ❌ Failed to setup agent audio capture:', error);
    }
  };

  const startRecording = async (stream: MediaStream, ws: WebSocket) => {
    try {
      // Create AudioContext to process raw audio samples
      const audioContext = new AudioContext({ sampleRate: 48000 });
      audioContextRef.current = audioContext;

      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch (e) {
          console.warn('[AgentAudio] AudioContext resume failed:', e);
        }
      }

      // Create MediaStreamSource from the microphone stream
      const source = audioContext.createMediaStreamSource(stream);

      // Create ScriptProcessor to get raw audio samples (2048 buffer size)
      const processor = audioContext.createScriptProcessor(2048, 1, 1);
      processorNodeRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (ws.readyState === WebSocket.OPEN) {
          // Get raw audio data
          const inputData = e.inputBuffer.getChannelData(0);
          
          // Convert Float32Array to Int16Array (linear16 PCM)
          const int16Data = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            // Clamp values and convert to 16-bit int
            const s = Math.max(-1, Math.min(1, inputData[i]));
            int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }

          // Send raw PCM data to Deepgram
          ws.send(int16Data.buffer);
        }
      };

      // Connect the nodes
      source.connect(processor);
      processor.connect(audioContext.destination);

      console.log('[AgentAudio] 🎙️ Audio processing started with Web Audio API');

    } catch (error) {
      console.error('[AgentAudio] Failed to start recording:', error);
    }
  };

  const cleanup = () => {
    console.log('[AgentAudio] 🧹 Cleaning up agent audio capture');

    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
    }

    if (deepgramWsRef.current) {
      if (deepgramWsRef.current.readyState === WebSocket.OPEN) {
        deepgramWsRef.current.close();
      }
      deepgramWsRef.current = null;
    }

  };
}
